import { useEffect, useState, type ReactNode } from 'react'
import type { SpareProdFamRow, SpareProdFilterRow } from '@alwindows/shared/api/spareParts'
import { trackSpareSearch } from '../analytics'

export type CatalogFilterState = {
  search: string
  prodFilters: Set<string>
  prodFams: Set<string>
}

type Props = {
  state: CatalogFilterState
  onChange: (next: CatalogFilterState) => void
  prodFilterOptions: SpareProdFilterRow[]
  prodFamOptions: SpareProdFamRow[]
}

const MOBILE_QUERY = '(max-width: 720px)'

// jsdom doesn't implement matchMedia; default to desktop layout in tests so the
// filter inputs are always queryable via getByLabelText without unwrapping an
// accordion.
function useIsMobile(): boolean {
  const get = () =>
    typeof window !== 'undefined' && typeof window.matchMedia === 'function'
      ? window.matchMedia(MOBILE_QUERY).matches
      : false
  const [match, setMatch] = useState(get)
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return
    const mq = window.matchMedia(MOBILE_QUERY)
    const update = () => setMatch(mq.matches)
    update()
    mq.addEventListener('change', update)
    return () => mq.removeEventListener('change', update)
  }, [])
  return match
}

export function CatalogFilters({ state, onChange, prodFilterOptions, prodFamOptions }: Props) {
  const isMobile = useIsMobile()

  // Legacy fired `spare_parts_search` on every search change; debounce so a
  // typed word produces one event, not one per keystroke. Skips the initial
  // empty render so a blank search never logs.
  const search = state.search.trim()
  useEffect(() => {
    if (!search) return
    const t = window.setTimeout(() => trackSpareSearch(search), 400)
    return () => window.clearTimeout(t)
  }, [search])

  const toggle = (set: Set<string>, value: string): Set<string> => {
    const next = new Set(set)
    if (next.has(value)) next.delete(value)
    else next.add(value)
    return next
  }

  const activeCount = state.prodFilters.size + state.prodFams.size + (state.search.trim() ? 1 : 0)

  const body: ReactNode = (
    <div className="al-cat-filters__body">
      <div className="al-cat-filters__group m-bx2">
        <label className="visuallyhidden" htmlFor="cat-search">
          Search
        </label>
        <input
          id="cat-search"
          type="search"
          className="input-text input-text--small"
          value={state.search}
          placeholder="Search…"
          onChange={(e) => onChange({ ...state, search: e.target.value })}
        />
      </div>

      <div className="al-cat-filters__group m-bx2">
        <p className="m-bx1">Category</p>
        {prodFilterOptions.map((opt) => {
          const id = `cat-prod-filter-${opt.Key}`
          return (
            <div className="al-cat-filters__row" key={opt.Key}>
              <input
                id={id}
                type="checkbox"
                className="input-checkbox"
                checked={state.prodFilters.has(opt.Key)}
                onChange={() =>
                  onChange({ ...state, prodFilters: toggle(state.prodFilters, opt.Key) })
                }
              />
              <label htmlFor={id} className="al-cat-filters__label">
                <span className="xsmall">{opt.Text}</span>
              </label>
            </div>
          )
        })}
      </div>

      <div className="al-cat-filters__group m-bx2">
        <p className="m-bx1">Window / door type</p>
        {prodFamOptions.map((opt) => {
          const id = `cat-prod-fam-${opt.Id}`
          return (
            <div className="al-cat-filters__row" key={opt.Id}>
              <input
                id={id}
                type="checkbox"
                className="input-checkbox"
                checked={state.prodFams.has(opt.Id)}
                onChange={() => onChange({ ...state, prodFams: toggle(state.prodFams, opt.Id) })}
              />
              <label htmlFor={id} className="al-cat-filters__label">
                <span className="xsmall">{opt.Desc}</span>
              </label>
            </div>
          )
        })}
      </div>
    </div>
  )

  if (isMobile) {
    return (
      <details className="al-cat-filters al-cat-filters--mobile">
        <summary className="al-cat-filters__summary">
          <span className="large no-m">Filter</span>
          {activeCount > 0 ? (
            <span className="al-cat-filters__count" aria-label={`${activeCount} active`}>
              {activeCount}
            </span>
          ) : null}
          <svg
            className="al-cat-filters__chev"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M6 9l6 6 6-6" />
          </svg>
        </summary>
        {body}
      </details>
    )
  }

  return (
    <aside className="al-cat-filters" aria-label="Catalog filters">
      <h3 className="large no-m">Filter</h3>
      {body}
    </aside>
  )
}
